/**
 * هر عدد، دو بار — و این تنها جایی است که شکلش تصمیم گرفته می‌شود.
 *
 * ═══ چرا انتخابگرِ پنجره حذف شد ═══
 *
 * تا امروز بالای هر سربرگ یک کشوییِ «۷ / ۳۰ / ۹۰ روز» بود (و در کارنامه،
 * انتخابگرِ اجرا). مشکلش این نبود که کار نمی‌کرد؛ این بود که **جوابِ
 * صفحه به چیزی بند بود که آدم یادش می‌رفت انتخاب کرده**. «۱۲ کاندیدا»
 * روی پنجره‌ی ۷ روز و «۱۲ کاندیدا» روی ۹۰ روز دو خبرِ کاملاً متفاوت‌اند و
 * هیچ‌کدام روی خودِ عدد نوشته نبود.
 *
 * حالا هر عدد همیشه دو تاست و برچسبِ هر دو کنارِ خودشان است:
 *
 *   ۱۰ روز      در ده روزِ گذشته چه گذشت
 *   آخرین اجرا  در همان یک اجرای آخر چه گذشت
 *
 * ⚠ **«آخرین اجرا» یعنی آخرین اجرای کامل**، نه تازه‌ترین ردیفِ
 * `crawler.pipeline_runs`. کارِ صفِ ادمین (بررسی/تست) و هر اجرای تک‌فازیِ
 * دستی هم ردیفِ اجرا می‌سازند و سه دقیقه‌اند؛ وقتی یکی از آن‌ها آخرین
 * ردیف بود، کلِ این ستون صفر و یک می‌شد. انتخاب حالا در
 * `public.crawler_last_run()` است — مایگریشنِ ۰۱۰۹.
 *
 * و هرجا معنی داشته باشد، «از N روی هم» ریز زیرشان — بی آن، «۱۲»
 * معلوم نمی‌کند از ۱۲ تا یا از ۳۰ هزار تا.
 *
 * ⚠ **«آخرین اجرا» روی جدولی که upsert می‌شود یعنی «دست‌خورده»، نه
 * «ساخته‌شده»** — کاندیدایی که در آن اجرا دوباره دیده شد هم می‌آید.
 * توضیحِ کامل در سرِ مایگریشنِ ۰۱۰۸.
 */
import React from 'react';
import type { Counts, Seconds } from '@/core/data/opsRepo';

export const fmtNum = (n: number | null | undefined) =>
  (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

/** برچسبِ ستونِ دوم. «فعالیتِ کاربران» اجرا ندارد و همین را عوض می‌کند. */
export const RUN_LABEL = 'آخرین اجرا';

/**
 * کارتِ آماری: دو عددِ درشت، برچسبِ ریزِ هرکدام، و جمعِ کل زیرشان.
 *
 * ⚠ `tone` فقط وقتی قرمز/سبز می‌کند که عددِ **۱۰ روز** غیرصفر باشد: خطای
 * دیروز که در آخرین اجرا تکرار نشده هنوز خطاست، ولی صفرِ ده‌روزه یعنی
 * واقعاً تمام شده.
 */
export function Stat({ label, v, tone, runLabel = RUN_LABEL, sub }: {
  label: React.ReactNode;
  v: Counts | undefined;
  tone?: 'warn' | 'good';
  runLabel?: string;
  sub?: React.ReactNode;
}) {
  const on = tone && (v?.d10 ?? 0) + (v?.run ?? 0) > 0 ? ` ${tone}` : '';
  return (
    <div className={`card stat pair${on}`}>
      <div className="pairline">
        <div>
          <div className="n">{fmtNum(v?.d10)}</div>
          <div className="w">۱۰ روز</div>
        </div>
        <div className="second">
          <div className="n">{fmtNum(v?.run)}</div>
          <div className="w">{runLabel}</div>
        </div>
      </div>
      <div className="k">{label}</div>
      {v?.total != null ? (
        <div className="tot">از {fmtNum(v.total)} روی هم</div>
      ) : null}
      {sub ? <div className="tot">{sub}</div> : null}
    </div>
  );
}

/**
 * همان دو عدد، در یک خانه‌ی جدول: `۱۰ روز / آخرین اجرا`.
 *
 * ⚠ عددِ کل این‌جا **نمی‌آید** و این عمدی است: ستونی که سه عدد در یک
 * خانه بنویسد دیگر ستون نیست. `title` هر سه را می‌گوید، برای وقتی که
 * کسی واقعاً لازمش دارد.
 */
export function Two({ v, bad, runLabel = RUN_LABEL }: {
  v: Counts | undefined; bad?: boolean; runLabel?: string;
}) {
  const title = `۱۰ روز: ${fmtNum(v?.d10)} · ${runLabel}: ${fmtNum(v?.run)}`
    + (v?.total != null ? ` · روی هم: ${fmtNum(v.total)}` : '');
  return (
    <span className={`two${bad && (v?.d10 ?? 0) > 0 ? ' bad' : ''}`} title={title}>
      {fmtNum(v?.d10)}
      <span className="dim"> / {fmtNum(v?.run)}</span>
    </span>
  );
}

/** ثانیه — و `null` یعنی «نچرخید»، نه «صفر ثانیه». */
export function TwoSeconds({ v, bad }: { v: Seconds | undefined; bad?: boolean }) {
  const one = (n: number | null | undefined) => (n != null ? `${n}s` : '—');
  return (
    <span className={`two${bad ? ' bad' : ''}`}
      title={`۱۰ روز: ${one(v?.d10)} · ${RUN_LABEL}: ${one(v?.run)} · روی هم: ${one(v?.total)}`}>
      {one(v?.d10)}
      <span className="dim"> / {one(v?.run)}</span>
    </span>
  );
}

/**
 * نوارِ یک‌خطیِ بالای هر سربرگ.
 *
 * روی هر صفحه‌ای که عددِ جفت دارد یک بار می‌آید. بدونش، ستونِ دوم شبیهِ
 * یک عددِ تصادفیِ کنارِ عددِ اصلی به نظر می‌رسد.
 */
export function PairLegend({ run, runLabel = RUN_LABEL, note }: {
  run?: {
    command: string | null; status: string | null; started_at: string; full?: boolean;
  } | null;
  runLabel?: string;
  note?: React.ReactNode;
}) {
  const when = run?.started_at
    ? new Date(run.started_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })
    : null;
  return (
    <p className="note legend">
      هر عدد دو تاست: سمتِ راست <b>۱۰ روزِ اخیر</b>، سمتِ چپ <b>{runLabel}</b>.
      {run ? (
        <>
          {' '}آخرین اجرای <b>کامل</b> (همان زنجیره‌ی شبانه){' '}
          <span className="mono">{run.command ?? '—'}</span> بود،
          {/* ⚠ تاریخِ لاتین در جمله‌ی راست‌به‌چپ باید جزیره‌ی خودش باشد،
              وگرنه «a.m.» از انتهای زمان کنده می‌شود و نقطه جای عوضی
              می‌نشیند — همان باگی که یک بار در جدولِ فعالیت افتاد. */}
          {' '}<span className="ltr">{when}</span>
          {run.status && run.status !== 'succeeded'
            ? <> — <span className="err">{run.status}</span></>
            : null}
          {/* ⚠ روی دیتابیسی که هرگز زنجیره‌ی کامل نداشته، ستونِ دوم مالِ یک
              اجرای تک‌فازی است و باید گفته شود — همان چیزی که تا مایگریشنِ
              ۰۱۰۹ بی‌صدا اتفاق می‌افتاد و ستون را صفر و یک می‌کرد. */}
          {run.full === false
            ? <> — <span className="err">هیچ اجرای کاملی ثبت نشده؛ این آخرین
                اجرای ناقص است</span></>
            : null}
        </>
      ) : null}
      {note ? <> {note}</> : null}
    </p>
  );
}
